import { useState, type FormEvent } from 'react';
import { Link, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { forgotBody, loginBody, registerBody, resetBody, type AuthResponse } from '@mafia/core';
import { api } from '@/lib/api';
import { useFormSubmit } from '@/lib/forms';
import { useAuth, banNotice } from '@/store/auth';
import { Alert, Button, Field, Input } from '@/components/ui';

export function AuthShell() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <Link to="/" className="mb-8 text-center">
        <div className="text-5xl">🎩</div>
        <h1 className="mt-3 font-display text-3xl tracking-wide">Мафия</h1>
        <p className="mt-1 text-sm text-zinc-500">Онлайн-ведущий для живой игры</p>
      </Link>
      <div className="card w-full max-w-sm p-6">
        <Outlet />
      </div>
      <Link to="/privacy" className="mt-6 text-xs text-zinc-500 hover:text-white">
        Политика обработки персональных данных
      </Link>
    </div>
  );
}

export function LoginPage() {
  const { signedIn, notice } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const { errors, formError, setFormError, pending, handle } = useFormSubmit(loginBody, async (data) => {
    try {
      signedIn(await api<AuthResponse>('/api/auth/login', { method: 'POST', body: data }));
    } catch (err) {
      const ban = banNotice(err);
      if (ban) setFormError(ban);
      else throw err;
    }
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void handle({ identifier, password });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h2 className="font-display text-xl">Вход</h2>
      {notice && !formError && <Alert tone="gold">{notice}</Alert>}
      {formError && <Alert>{formError}</Alert>}
      <Field label="Email или логин" error={errors.identifier}>
        <Input autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoFocus />
      </Field>
      <Field label="Пароль" error={errors.password}>
        <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Button type="submit" className="w-full" loading={pending}>
        Войти
      </Button>
      <div className="flex justify-between text-sm">
        <Link to="/register" className="link">
          Регистрация
        </Link>
        <Link to="/forgot-password" className="text-zinc-400 hover:text-white">
          Забыли пароль?
        </Link>
      </div>
    </form>
  );
}

const registerForm = registerBody
  .extend({ confirm: z.string() })
  .refine((v) => v.password === v.confirm, { message: 'Пароли не совпадают', path: ['confirm'] });

export function RegisterPage() {
  const { signedIn } = useAuth();
  const [form, setForm] = useState({ email: '', login: '', nickname: '', password: '', confirm: '' });
  const [pdConsent, setPdConsent] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  const { errors, formError, pending, handle } = useFormSubmit(registerForm, async ({ confirm: _confirm, ...body }) => {
    signedIn(await api<AuthResponse>('/api/auth/register', { method: 'POST', body }));
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handle({ ...form, nickname: form.nickname.trim() || undefined, pdConsent });
      }}
      className="space-y-4"
      noValidate
    >
      <h2 className="font-display text-xl">Регистрация</h2>
      {formError && <Alert>{formError}</Alert>}
      <Field label="Email" error={errors.email} hint="Нужен только для восстановления пароля">
        <Input type="email" autoComplete="email" value={form.email} onChange={set('email')} autoFocus />
      </Field>
      <Field label="Логин" error={errors.login} hint="Латиница, цифры и _, для входа">
        <Input autoComplete="username" value={form.login} onChange={set('login')} />
      </Field>
      <Field label="Никнейм (необязательно)" error={errors.nickname} hint="Как вас увидят за столом">
        <Input value={form.nickname} onChange={set('nickname')} />
      </Field>
      <Field label="Пароль" error={errors.password}>
        <Input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} />
      </Field>
      <Field label="Повторите пароль" error={errors.confirm}>
        <Input type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
      </Field>
      <div>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/5 bg-ink-850 px-3.5 py-3">
          <input
            type="checkbox"
            checked={pdConsent}
            onChange={(e) => setPdConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-gold-400"
          />
          <span className="text-sm text-zinc-300">
            Даю{' '}
            <Link to="/consent" target="_blank" className="link">
              согласие на обработку персональных данных
            </Link>{' '}
            и принимаю{' '}
            <Link to="/privacy" target="_blank" className="link">
              политику обработки
            </Link>
          </span>
        </label>
        {errors.pdConsent && <span className="mt-1 block text-sm text-blood-400">{errors.pdConsent}</span>}
      </div>
      <Button type="submit" className="w-full" loading={pending}>
        Создать аккаунт
      </Button>
      <p className="text-center text-sm text-zinc-400">
        Уже есть аккаунт?{' '}
        <Link to="/login" className="link">
          Войти
        </Link>
      </p>
    </form>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { errors, formError, pending, handle } = useFormSubmit(forgotBody, async (body) => {
    await api('/api/auth/forgot', { method: 'POST', body });
    setSent(true);
  });

  if (sent) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl">Проверьте почту</h2>
        <p className="text-sm text-zinc-400">
          Если аккаунт с адресом <b className="text-zinc-200">{email}</b> существует, мы отправили на него ссылку для сброса пароля.
          Она действует 30 минут.
        </p>
        <Link to="/login" className="link text-sm">
          Вернуться ко входу
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handle({ email });
      }}
      className="space-y-4"
      noValidate
    >
      <h2 className="font-display text-xl">Восстановление пароля</h2>
      {formError && <Alert>{formError}</Alert>}
      <Field label="Email" error={errors.email}>
        <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </Field>
      <Button type="submit" className="w-full" loading={pending}>
        Отправить ссылку
      </Button>
      <Link to="/login" className="block text-center text-sm text-zinc-400 hover:text-white">
        Назад
      </Link>
    </form>
  );
}

const resetForm = resetBody
  .extend({ confirm: z.string() })
  .refine((v) => v.password === v.confirm, { message: 'Пароли не совпадают', path: ['confirm'] });

export function ResetPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { logout, status } = useAuth();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const { errors, formError, pending, handle } = useFormSubmit(resetForm, async ({ token, password }) => {
    await api('/api/auth/reset', { method: 'POST', body: { token, password } });
    if (status === 'authed') await logout();
    setDone(true);
  });

  if (!token) return <Alert>Ссылка некорректна. Запросите сброс пароля ещё раз.</Alert>;
  if (done) {
    return (
      <div className="space-y-4">
        <Alert tone="green">Пароль изменён. Все остальные сеансы завершены.</Alert>
        <Button className="w-full" onClick={() => navigate('/login')}>
          Войти
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handle({ token, password, confirm });
      }}
      className="space-y-4"
      noValidate
    >
      <h2 className="font-display text-xl">Новый пароль</h2>
      {(formError || errors.token) && <Alert>{formError ?? 'Ссылка недействительна'}</Alert>}
      <Field label="Новый пароль" error={errors.password}>
        <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
      </Field>
      <Field label="Повторите пароль" error={errors.confirm}>
        <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      <Button type="submit" className="w-full" loading={pending}>
        Сохранить пароль
      </Button>
    </form>
  );
}
